import { ensureCsrfToken, getCsrfToken } from './csrf';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `Request failed with ${status}`);
  }
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (UNSAFE_METHODS.has(method)) {
    const token = getCsrfToken() || (await ensureCsrfToken());
    if (token) headers.set('X-CSRFToken', token);
  }

  // Only set JSON content-type when a body is present and caller didn't already
  // pick one (e.g. FormData uploads must set their own multipart boundary).
  if (
    init.body &&
    !(init.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(path, {
    ...init,
    method,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const body = await response
      .clone()
      .json()
      .catch(() => response.clone().text().catch(() => null));
    throw new ApiError(response.status, body);
  }

  return response;
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(path, init);
  return (await response.json()) as T;
}
